import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";

type Status = "idle" | "loading" | "success" | "error";

const GUARD_URL = "https://hakezpdaxzprbealfpjo.supabase.co/functions/v1/admin-login-guard";
const RESET_URL = "https://hakezpdaxzprbealfpjo.supabase.co/functions/v1/admin-password-reset";

async function checkRateLimit(): Promise<{ blocked: boolean; remaining: number; minutes_left?: number }> {
  const res = await fetch(GUARD_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "check" }),
  });
  return res.json();
}

async function recordAttempt(result: "success" | "failure") {
  await fetch(GUARD_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "record", result }),
  });
}

export default function AdminLoginPage() {
  const { signIn, user, profile, loading: authLoading, emailConfirmed, resendConfirmation, resetPassword } = useAuth();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [rateBlocked, setRateBlocked] = useState(false);
  const [rateMinutesLeft, setRateMinutesLeft] = useState(0);
  const [rateRemaining, setRateRemaining] = useState(5);
  const [resendStatus, setResendStatus] = useState<"idle" | "loading" | "sent">("idle");
  const [showEmergencyReset, setShowEmergencyReset] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStatus, setForgotStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [forgotMsg, setForgotMsg] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetPasswordVal, setResetPasswordVal] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetStatus, setResetStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [resetMsg, setResetMsg] = useState("");

  // Check rate limit on mount
  useEffect(() => {
    checkRateLimit().then((data) => {
      if (data.blocked) {
        setRateBlocked(true);
        setRateMinutesLeft(data.minutes_left || 15);
      } else {
        setRateRemaining(data.remaining);
      }
    });
  }, []);

  // Countdown for blocked state
  useEffect(() => {
    if (!rateBlocked || rateMinutesLeft <= 0) return;
    const timer = setInterval(() => {
      setRateMinutesLeft((prev) => {
        if (prev <= 1) {
          setRateBlocked(false);
          setRateRemaining(5);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 60000);
    return () => clearInterval(timer);
  }, [rateBlocked, rateMinutesLeft]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      setErrorMsg("");
      return;
    }

    const email = (formData.get("email") as string) || "";
    const password = (formData.get("password") as string) || "";

    // Check rate limit before attempting login
    const limit = await checkRateLimit();
    if (limit.blocked) {
      setStatus("error");
      setErrorMsg(`Too many login attempts. Please try again in ${limit.minutes_left} minute${limit.minutes_left !== 1 ? "s" : ""}.`);
      setRateBlocked(true);
      setRateMinutesLeft(limit.minutes_left || 15);
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    const { error } = await signIn(email, password);

    if (error) {
      setStatus("error");
      setErrorMsg(error.message || error);
      await recordAttempt("failure");
      // Refresh remaining count
      const updated = await checkRateLimit();
      setRateRemaining(updated.remaining);
      if (updated.blocked) {
        setRateBlocked(true);
        setRateMinutesLeft(updated.minutes_left || 15);
        setErrorMsg(`Too many login attempts. Please try again in ${updated.minutes_left} minute${updated.minutes_left !== 1 ? "s" : ""}.`);
      }
      return;
    }

    // Sign-in succeeded
    await recordAttempt("success");
    setLoggedIn(true);
  };

  // Once profile loads after signIn, check role and auto-redirect
  useEffect(() => {
    if (loggedIn && !authLoading && user && profile) {
      if (profile.role === "admin") {
        setStatus("success");
        // Auto-redirect to admin dashboard after a brief moment
        const redirectTimer = setTimeout(() => {
          navigate("/admin", { replace: true });
        }, 800);
        return () => clearTimeout(redirectTimer);
      } else {
        setStatus("error");
        setErrorMsg("Access denied. This login is for administrators only.");
        recordAttempt("failure");
      }
      setLoggedIn(false);
    }
  }, [loggedIn, authLoading, user, profile, navigate]);

  const handleResendConfirmation = async () => {
    if (!user?.email) return;
    setResendStatus("loading");
    const { error } = await resendConfirmation(user.email);
    if (error) {
      setResendStatus("idle");
    } else {
      setResendStatus("sent");
    }
  };

  // Already logged in as admin — auto-redirect immediately
  const alreadyAdmin = user && profile?.role === "admin" && status !== "success";

  useEffect(() => {
    if (alreadyAdmin) {
      navigate("/admin", { replace: true });
    }
  }, [alreadyAdmin, navigate]);

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setForgotStatus("success");
      setForgotMsg("If an account exists with this email, a password reset link has been sent.");
      return;
    }

    const emailVal = (formData.get("forgot_email") as string) || "";

    if (!emailVal) {
      setForgotStatus("error");
      setForgotMsg("Please enter your email address.");
      return;
    }

    setForgotStatus("loading");
    setForgotMsg("");

    const { error } = await resetPassword(emailVal);

    if (error) {
      setForgotStatus("error");
      setForgotMsg(error);
    } else {
      setForgotStatus("success");
      setForgotMsg("If an account exists with this email, a password reset link has been sent. Check your inbox (and spam folder).");
      if (formRef.current) form.reset();
    }
  };

  const handleEmergencyReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setResetStatus("success");
      setResetMsg("Password has been reset. You can now log in with your new password.");
      return;
    }

    const emailVal = (formData.get("reset_email") as string) || "";
    const pwVal = (formData.get("reset_password") as string) || "";
    const confirmVal = (formData.get("reset_confirm") as string) || "";

    if (pwVal !== confirmVal) {
      setResetStatus("error");
      setResetMsg("Passwords do not match.");
      return;
    }

    if (pwVal.length < 8) {
      setResetStatus("error");
      setResetMsg("Password must be at least 8 characters.");
      return;
    }

    setResetStatus("loading");
    setResetMsg("");

    try {
      const res = await fetch(RESET_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailVal, password: pwVal }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setResetStatus("success");
        setResetMsg("Password has been reset. You can now log in with your new password.");
        if (formRef.current) form.reset();
      } else {
        setResetStatus("error");
        setResetMsg(data.error || "Failed to reset password. Please try again.");
      }
    } catch {
      setResetStatus("error");
      setResetMsg("Network error. Please check your connection and try again.");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0d0d1a]">
        <i className="fas fa-spinner fa-spin text-[#5cb85c] text-2xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d1a] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: "radial-gradient(circle, #5cb85c 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="relative w-full max-w-md z-10">
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#5cb85c] rounded-2xl mb-4">
            <i className="fas fa-shield-halved text-white text-2xl" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">HACKERS LODGE</h1>
          <p className="text-sm text-gray-500 mt-1 uppercase tracking-[0.2em]">Admin Panel</p>
        </div>

        {/* Login card */}
        {!showForgotPassword ? (
          <div className="bg-[#1a1a2e] rounded-xl border border-white/10 overflow-hidden">
            <div className="px-6 pt-6 pb-2">
              <h2 className="text-base font-semibold text-white">Sign In</h2>
              <p className="text-xs text-gray-500 mt-1">Enter your credentials to access the admin dashboard.</p>
            </div>

            <div className="px-6 pb-6">
              {/* Email Verification Banner */}
              {user && !emailConfirmed && !alreadyAdmin && (
                <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="fas fa-envelope text-amber-400 text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-amber-400 mb-1">Email not confirmed</p>
                      <p className="text-xs text-amber-400/80 leading-relaxed mb-2">
                        Check <strong>{user.email}</strong> for the confirmation link before proceeding.
                      </p>
                      <button
                        type="button"
                        onClick={handleResendConfirmation}
                        disabled={resendStatus !== "idle"}
                        className="text-xs font-semibold text-amber-400 hover:text-amber-300 underline transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        {resendStatus === "loading" ? (
                          <span><i className="fas fa-spinner fa-spin mr-1" />Sending...</span>
                        ) : resendStatus === "sent" ? (
                          <span><i className="fas fa-check mr-1" />Resent! Check your inbox</span>
                        ) : (
                          <span>Resend confirmation email</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <form ref={formRef} onSubmit={handleSubmit}>
                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                />

                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
                    Email Address
                  </label>
                  <div className="relative">
                    <i className="fas fa-envelope absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="admin@hackerslodge.com"
                      className="w-full pl-10 pr-3 py-2.5 bg-[#0d0d1a] border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#5cb85c]/50 transition-colors"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
                    Password
                  </label>
                  <div className="relative">
                    <i className="fas fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                    <input
                      type="password"
                      name="password"
                      required
                      placeholder="••••••••"
                      className="w-full pl-10 pr-3 py-2.5 bg-[#0d0d1a] border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#5cb85c]/50 transition-colors"
                      disabled={rateBlocked}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between mb-5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="remember_me" className="rounded accent-[#5cb85c]" disabled={rateBlocked} />
                    <span className="text-xs text-gray-500">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setForgotStatus("idle");
                      setForgotMsg("");
                      setForgotEmail("");
                    }}
                    className="text-xs text-gray-500 hover:text-[#5cb85c] transition-colors cursor-pointer"
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Rate-limit warning */}
                {rateBlocked && (
                  <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm text-yellow-400 flex items-start gap-2">
                    <i className="fas fa-hourglass-half mt-0.5 shrink-0" />
                    <span>Too many attempts. Try again in {rateMinutesLeft} minute{rateMinutesLeft !== 1 ? "s" : ""}.</span>
                  </div>
                )}

                {/* Remaining attempts hint (only show when low) */}
                {!rateBlocked && rateRemaining <= 2 && status === "idle" && (
                  <div className="mb-4 p-2 bg-yellow-500/5 border border-yellow-500/20 rounded text-xs text-yellow-500/80 text-center">
                    <i className="fas fa-exclamation-circle mr-1" />
                    {rateRemaining} attempt{rateRemaining !== 1 ? "s" : ""} remaining before lockout
                  </div>
                )}

                {status === "error" && errorMsg && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400 flex items-start gap-2">
                    <i className="fas fa-exclamation-triangle mt-0.5 shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                {status === "success" && (
                  <div className="mb-4 p-3 bg-[#5cb85c]/10 border border-[#5cb85c]/30 rounded-lg text-sm">
                    <p className="text-[#5cb85c] flex items-center gap-2">
                      <i className="fas fa-check-circle" />
                      Login successful! Redirecting to dashboard...
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading" || rateBlocked}
                  className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded-lg transition-all whitespace-nowrap"
                >
                  {rateBlocked ? (
                    <span><i className="fas fa-lock mr-2" />Locked ({rateMinutesLeft}m)</span>
                  ) : status === "loading" ? (
                    <span><i className="fas fa-spinner fa-spin mr-2" />Authenticating...</span>
                  ) : (
                    <span><i className="fas fa-sign-in-alt mr-2" />SIGN IN</span>
                  )}
                </button>
              </form>
            </div>
          </div>
        ) : (
          /* Forgot Password Card */
          <div className="bg-[#1a1a2e] rounded-xl border border-white/10 overflow-hidden">
            <div className="px-6 pt-6 pb-2">
              <h2 className="text-base font-semibold text-white">Reset Your Password</h2>
              <p className="text-xs text-gray-500 mt-1">
                Enter your admin email and we&apos;ll send you a password reset link.
              </p>
            </div>

            <div className="px-6 pb-6">
              <form onSubmit={handleForgotPassword}>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                />

                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
                    Email Address
                  </label>
                  <div className="relative">
                    <i className="fas fa-envelope absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                    <input
                      type="email"
                      name="forgot_email"
                      required
                      placeholder="mjean@gmx.us"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 bg-[#0d0d1a] border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#5cb85c]/50 transition-colors"
                    />
                  </div>
                </div>

                {forgotStatus === "error" && forgotMsg && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400 flex items-start gap-2">
                    <i className="fas fa-exclamation-triangle mt-0.5 shrink-0" />
                    <span>{forgotMsg}</span>
                  </div>
                )}

                {forgotStatus === "success" && forgotMsg && (
                  <div className="mb-4 p-3 bg-[#5cb85c]/10 border border-[#5cb85c]/30 rounded-lg text-sm text-[#5cb85c] flex items-start gap-2">
                    <i className="fas fa-check-circle mt-0.5 shrink-0" />
                    <span>{forgotMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={forgotStatus === "loading"}
                  className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded-lg transition-all whitespace-nowrap"
                >
                  {forgotStatus === "loading" ? (
                    <span><i className="fas fa-spinner fa-spin mr-2" />Sending reset link...</span>
                  ) : (
                    <span><i className="fas fa-paper-plane mr-2" />SEND RESET LINK</span>
                  )}
                </button>
              </form>

              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotStatus("idle");
                  setForgotMsg("");
                }}
                className="w-full mt-3 text-xs text-gray-500 hover:text-gray-400 transition-colors text-center cursor-pointer"
              >
                <i className="fas fa-arrow-left mr-1" />Back to Sign In
              </button>
            </div>
          </div>
        )}

        {/* Emergency Password Reset */}
        <div className="mt-6">
          {!showEmergencyReset ? (
            <button
              type="button"
              onClick={() => setShowEmergencyReset(true)}
              className="w-full text-xs text-gray-600 hover:text-gray-400 transition-colors text-center block cursor-pointer"
            >
              <i className="fas fa-key mr-1" />Emergency Admin Password Reset
            </button>
          ) : (
            <div className="bg-[#1a1a2e] rounded-xl border border-orange-500/30 overflow-hidden">
              <div className="px-6 pt-4 pb-2">
                <div className="flex items-center gap-2">
                  <i className="fas fa-exclamation-triangle text-orange-400 text-sm" />
                  <h3 className="text-sm font-semibold text-orange-400">Emergency Password Reset</h3>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Use this only if you can&apos;t receive the password reset email. Sets a new password directly.
                </p>
              </div>

              <div className="px-6 pb-5">
                <form onSubmit={handleEmergencyReset}>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                  />

                  <div className="mb-3">
                    <label className="block text-xs font-semibold text-gray-400 mb-1.5">Admin Email</label>
                    <input
                      type="email"
                      name="reset_email"
                      required
                      placeholder="mjean@gmx.us"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full px-3 py-2 bg-[#0d0d1a] border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500/50 transition-colors"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-semibold text-gray-400 mb-1.5">New Password (min 8 chars)</label>
                    <input
                      type="password"
                      name="reset_password"
                      required
                      minLength={8}
                      placeholder="••••••••"
                      value={resetPasswordVal}
                      onChange={(e) => setResetPasswordVal(e.target.value)}
                      className="w-full px-3 py-2 bg-[#0d0d1a] border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500/50 transition-colors"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-semibold text-gray-400 mb-1.5">Confirm New Password</label>
                    <input
                      type="password"
                      name="reset_confirm"
                      required
                      minLength={8}
                      placeholder="••••••••"
                      value={resetConfirm}
                      onChange={(e) => setResetConfirm(e.target.value)}
                      className="w-full px-3 py-2 bg-[#0d0d1a] border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500/50 transition-colors"
                    />
                  </div>

                  {resetStatus === "error" && resetMsg && (
                    <div className="mb-3 p-2.5 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400 flex items-start gap-2">
                      <i className="fas fa-exclamation-circle mt-0.5 shrink-0" />
                      <span>{resetMsg}</span>
                    </div>
                  )}

                  {resetStatus === "success" && resetMsg && (
                    <div className="mb-3 p-2.5 bg-green-500/10 border border-green-500/30 rounded-lg text-xs text-green-400 flex items-start gap-2">
                      <i className="fas fa-check-circle mt-0.5 shrink-0" />
                      <span>{resetMsg}</span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={resetStatus === "loading"}
                      className="flex-1 bg-orange-600 hover:bg-orange-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-xs font-semibold py-2 rounded-lg transition-all whitespace-nowrap"
                    >
                      {resetStatus === "loading" ? (
                        <span><i className="fas fa-spinner fa-spin mr-1" />Resetting...</span>
                      ) : (
                        <span><i className="fas fa-key mr-1" />RESET PASSWORD</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEmergencyReset(false);
                        setResetStatus("idle");
                        setResetMsg("");
                        setResetEmail("");
                        setResetPasswordVal("");
                        setResetConfirm("");
                      }}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* Footer links */}
        <div className="text-center mt-6">
          <Link to="/" className="text-xs text-gray-400 hover:text-gray-400 transition-colors">
            <i className="fas fa-arrow-left mr-1" />Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
}